import React, { useState, useRef, useCallback } from "react";

interface CustomSliderProps {
  min: number;
  max: number;
  value: number | [number, number];
  onChange: (value: number | [number, number]) => void;
  orientation?: "horizontal" | "vertical";
  className?: string;
}

export const TotalHouseholdsByZipCodeChartSlider: React.FC<
  CustomSliderProps
> = ({
  min,
  max,
  value,
  onChange,
  orientation = "horizontal",
  className = "",
}) => {
  const [isDragging, setIsDragging] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const isRange = Array.isArray(value);
  const [minValue, maxValue] = isRange ? value : [min, value];

  const calculateValue = useCallback(
    (clientX: number, clientY: number) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();

      if (orientation === "horizontal") {
        const thumbWidth = 48;
        const halfThumb = thumbWidth / 2;
        const availableWidth = rect.width - thumbWidth;
        const x = Math.max(
          halfThumb,
          Math.min(clientX - rect.left, rect.width - halfThumb),
        );
        const adjustedX = x - halfThumb;
        const percentage = adjustedX / availableWidth;
        return Math.round(percentage * (max - min) + min);
      } else {
        const thumbHeight = 40;
        const halfThumb = thumbHeight / 2;
        const availableHeight = rect.height - thumbHeight;
        const y = Math.max(
          halfThumb,
          Math.min(clientY - rect.top, rect.height - halfThumb),
        );
        const adjustedY = y - halfThumb;
        const percentage = (availableHeight - adjustedY) / availableHeight;
        return Math.round(percentage * (max - min) + min);
      }
    },
    [min, max, orientation],
  );

  const getClosestThumb = useCallback(
    (clientX: number, clientY: number) => {
      if (!isRange) return 0;
      const newValue = calculateValue(clientX, clientY);
      if (newValue === undefined) return 0;

      const distanceToMin = Math.abs(newValue - minValue);
      const distanceToMax = Math.abs(newValue - maxValue);
      return distanceToMin <= distanceToMax ? 0 : 1;
    },
    [isRange, calculateValue, minValue, maxValue],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const thumbIndex = isRange ? getClosestThumb(e.clientX, e.clientY) : 0;
      setIsDragging(thumbIndex);

      const newValue = calculateValue(e.clientX, e.clientY);
      if (newValue !== undefined) {
        if (isRange) {
          const newRange: [number, number] =
            thumbIndex === 0
              ? [Math.min(newValue, maxValue - 1), maxValue]
              : [minValue, Math.max(newValue, minValue + 1)];
          onChange(newRange);
        } else {
          onChange(newValue);
        }
      }
    },
    [calculateValue, onChange, isRange, getClosestThumb, minValue, maxValue],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging === null) return;
      const newValue = calculateValue(e.clientX, e.clientY);
      if (newValue !== undefined) {
        if (isRange) {
          const newRange: [number, number] =
            isDragging === 0
              ? [Math.min(newValue, maxValue - 1), maxValue]
              : [minValue, Math.max(newValue, minValue + 1)];
          onChange(newRange);
        } else {
          onChange(newValue);
        }
      }
    },
    [isDragging, calculateValue, onChange, isRange, minValue, maxValue],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(null);
    setIsHovered(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const minPercentage = (minValue - min) / (max - min);
  const maxPercentage = (maxValue - min) / (max - min);

  const getThumbStyle = (percentage: number) => {
    return orientation === "horizontal"
      ? {
          left: `calc(${percentage} * (100% - 48px))`,
          width: "48px",
          pointerEvents: "none" as const,
        }
      : {
          bottom: `calc(${percentage} * (100% - 40px))`,
          height: "40px",
          pointerEvents: "none" as const,
        };
  };

  const containerClasses =
    orientation === "horizontal"
      ? `h-10 border rounded-lg relative cursor-pointer select-none transition-all overflow-visible ${
          isDragging !== null
            ? "bg-gray-200 shadow-inner"
            : "bg-gray-50 hover:bg-gray-200"
        } ${className}`
      : `w-10 border rounded-lg relative cursor-pointer select-none transition-all overflow-visible ${
          isDragging !== null
            ? "bg-gray-200 shadow-inner"
            : "bg-gray-50 hover:bg-gray-200"
        } ${className || "h-96"}`;

  const getThumbClasses = (thumbIndex: number) => {
    const baseClasses =
      orientation === "horizontal"
        ? `h-full bg-blue-200 border-2 border-blue-500 rounded absolute flex items-center justify-center transition-all duration-150`
        : `w-full bg-blue-200 border-2 border-blue-500 rounded absolute flex items-center justify-center transition-all duration-150`;

    const activeClasses =
      isDragging === thumbIndex ? "bg-blue-300 border-blue-600 shadow-lg" : "";
    return `${baseClasses} ${activeClasses}`;
  };

  const indicatorElement =
    orientation === "horizontal" ? (
      <div className="w-1 h-6 bg-blue-600 rounded-full opacity-70" />
    ) : (
      <div className="w-6 h-1 bg-blue-600 rounded-full opacity-70" />
    );

  const formatNumber = (num: number) => new Intl.NumberFormat().format(num);

  return (
    <div
      className={containerClasses}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      ref={sliderRef}
    >
      {isRange ? (
        <>
          <div
            className={getThumbClasses(0)}
            style={getThumbStyle(minPercentage)}
          >
            {indicatorElement}
          </div>
          <div
            className={getThumbClasses(1)}
            style={getThumbStyle(maxPercentage)}
          >
            {indicatorElement}
          </div>
        </>
      ) : (
        /* Single Thumb */
        <div
          className={getThumbClasses(0)}
          style={getThumbStyle(maxPercentage)}
        >
          {indicatorElement}
        </div>
      )}

      {isHovered && (
        <div
          className={
            orientation === "horizontal"
              ? "absolute top-full mt-2 z-10 left-1/2 -translate-x-1/2"
              : "absolute left-full ml-2 z-10 top-1/2 -translate-y-1/2"
          }
        >
          <div className="min-w-[8rem] whitespace-nowrap text-sm p-3 rounded border shadow-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-600">
            <div className="font-semibold mb-1">
              {orientation === "horizontal" ? "Bars Shown" : "Households"}
            </div>
            <div>
              {isRange
                ? `${formatNumber(minValue)} - ${formatNumber(maxValue)}`
                : orientation === "horizontal"
                  ? `${value} of ${max}`
                  : `${formatNumber(value as number)} (${formatNumber(min)} - ${formatNumber(max)})`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
